// src/RootComponent.js
import React, { useEffect, useState } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  useParams,
  useLocation,
  useNavigationType,
  createRoutesFromChildren,
  matchRoutes,
} from "react-router-dom";
import { LoadingOverlay, Box, Text, Center } from "@mantine/core";
import App from "./App";
import { DataProvider } from "./context/DataContext";
import NotFound from "./pages/not-found";
import SubRoutes from "./routes";
import {
  FpjsProvider,
  FingerprintJSPro,
} from "@fingerprintjs/fingerprintjs-pro-react";
import * as Sentry from "@sentry/react"; // Import Sentry
import { ensureTrailingSlash } from "./utils/url";
import { getEdgeSessionUrl, getEdgeRetrieveUrl } from "./utils/edge";
import {
  browserName,
  browserVersion,
  fullBrowserVersion,
  osName,
  osVersion,
  deviceType,
  mobileVendor,
  mobileModel,
  getUA
} from "react-device-detect";

// Sentry is currently disabled
// To re-enable, uncomment the Sentry initialization below and the SentryRoutes variable
/*
Sentry.init({
  dsn: "https://e5bbbf0db439485e92518f5484e1baae@o4504792099258368.ingest.us.sentry.io/4504792101158912",
  integrations: [
    Sentry.reactRouterV6BrowserTracingIntegration({
      useEffect: React.useEffect,
      useLocation,
      useNavigationType,
      createRoutesFromChildren,
      matchRoutes,
    }),
    Sentry.breadcrumbsIntegration({
      console: true,
      dom: true,
      fetch: true,
      history: true,
      xhr: true,
    }),
    Sentry.replayIntegration(),
    Sentry.captureConsoleIntegration(),
  ],
  tracesSampleRate: 1.0, // Adjust based on your need
  tracePropagationTargets: [
    /^https:\/\/fe\.dcams\.app/, // Match fe.dcams.app
    /^https:\/\/vx-fe\.dcams\.app/, // Match vx-fe.dcams.app
  ],

  // Capture Replay for 10% of all sessions,
  // plus for 100% of sessions with an error
  replaysSessionSampleRate: 0.1,
  replaysOnErrorSampleRate: 1.0,
});
*/

// const SentryRoutes = Sentry.withSentryReactRouterV6Routing(Routes);

// Function to get request_id from session_id
async function getRequestIdFromSession(session_id) {
  const response = await fetch(
    process.env.REACT_APP_BACKEND_URL + '/v3/stable/api/frontend/exchangeSessionForToken',
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ session_id }) // Payload with the session_id
    }
  )

  if (!response.ok) {
    throw new Error(`Error exchanging session: ${response.statusText}`)
  }

  // Sample response: { "request_id": "1f9dfa59-54fa-4067-9a29-26265342b2f2" }
  const result = await response.json()
  return result.request_id
}

const AppWithData = () => {
  const { request_id } = useParams();
  const location = useLocation();
  const [sessionData, setSessionData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Check fingerprint param directly in the render function to avoid timing issues
  const urlParams = new URLSearchParams(location.search);
  const fingerprintParam = urlParams.get('fingerprint');
  const shouldDisableFingerprint = fingerprintParam === 'f';
  
  // Sentry tag setting is currently disabled
  /*
  useEffect(() => {
    if (request_id) {
      // Set request_id as a tag in Sentry
      Sentry.setTag("request_id", request_id);
    }
  }, [request_id]);
  */

  // Check for frontend query param
  const frontendParam = urlParams.get('frontend');
  const edge = urlParams.get('edge');
  const stateParam = urlParams.get('state');

  // Determine if we should use VX backend based on hostname or query param
  const useVxBackend = window.location.hostname === "vx-fe.dcams.app" || frontendParam === "vx-fe";

  // Check if edge parameter is true OR if request_id/state carries the "-edge" suffix
  const isEdgeFromRequestId = request_id?.endsWith('-edge') || stateParam?.endsWith('-edge');
  const useEdgeEndpoint = edge === "true" || isEdgeFromRequestId;
  
  const edgeSessionUrl = getEdgeSessionUrl();
  const vxSessionUrl = ensureTrailingSlash(
    "https://vx.dcams.app/frontend/session/get_session"
  );

  const getPrimaryUrl = useEdgeEndpoint
    ? edgeSessionUrl
    : useVxBackend
    ? vxSessionUrl
    : `${process.env.REACT_APP_BACKEND_URL}/v3/stable/api/frontend/getUrl`;

  const getSecondaryUrl = useEdgeEndpoint
    ? edgeSessionUrl
    : !useVxBackend
    ? vxSessionUrl
    : `${process.env.REACT_APP_BACKEND_URL}/v3/stable/api/frontend/getUrl`;

  const fetchAndCheck = async (url, request_id, isEdge = false) => {
    try {
      if (isEdge && !request_id) {
        console.warn("Edge session request attempted without a request_id");
      }

      // Build payload with device_info when edge mode is active
      const payload = { request_id };

      if (isEdge) {
        payload.device_info = {
          browserName,
          browserVersion,
          fullBrowserVersion,
          osName,
          osVersion,
          deviceType,
          mobileVendor,
          mobileModel,
          userAgent: getUA
        };

        // Include current URL parameters in edge mode
        const currentUrlParams = new URLSearchParams(window.location.search);
        const urlParamsObject = {};
        for (const [key, value] of currentUrlParams.entries()) {
          urlParamsObject[key] = value;
        }
        if (Object.keys(urlParamsObject).length > 0) {
          payload.url_params = urlParamsObject;
        }
      }

      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      // Return specific indicators for different response types
      if (response.status === 404) {
        return { notFound: true };
      }

      if (!response.ok) {
        console.warn(`Error with request to ${url}: ${response.statusText}`);
        return null;
      }

      const result = await response.json();

      // Check for edge "Not Found" error in response body
      if (isEdge && result.error === "Not Found" &&
          result.message === "No session data found for the given request ID.") {
        return { notFound: true };
      }

      // Allow already_done responses to pass through even without data
      if ((!result.data || result.data === false) && result.already_done !== true) {
        return null;
      }

      return result;
    } catch (error) {
      console.warn(`Failed to fetch from ${url}:`, error);
      return null;
    }
  };

  // Fetch the session data first
  useEffect(() => {
    const fetchData = async () => {
      if (!request_id || request_id === 'upload') {
        setIsLoading(false);
        return;
      }

      let localRequestId = request_id;

      // Handle special cases for request_id
      if (localRequestId === 'clear' || localRequestId === 'clear/return' ||
          localRequestId === 'oneid' || localRequestId === 'oneid/return' ||
          localRequestId === 'digilocker' || localRequestId === 'digilocker/return/success' ||
          localRequestId === 'digilocker/return/failure') {
        localRequestId = urlParams.get('state');
        if (localRequestId?.endsWith('-edge')) {
          localRequestId = localRequestId.replace('-edge', '');
        }
      } else if (localRequestId === 'mdl') {
        const session_id = urlParams.get('sessionId');
        if (!session_id) {
          setError('No session_id in query params.');
          setIsLoading(false);
          return;
        }
        try {
          localRequestId = await getRequestIdFromSession(session_id);
        } catch (err) {
          setError('Could not exchange session for token');
          setIsLoading(false);
          return;
        }
      } else if (localRequestId === 'trinsic' || localRequestId === 'bankid-cz' ||
                 localRequestId === 'finnish-trust-network' || localRequestId === 'mojeid' ||
                 localRequestId === 'brazil-cpf') {
        // Trinsic providers use sessionId to exchange for real request_id
        const session_id = urlParams.get('sessionId');
        if (!session_id) {
          setError('No sessionId in query params for Trinsic return.');
          setIsLoading(false);
          return;
        }
        try {
          localRequestId = await getRequestIdFromSession(session_id);
          // If exchange returns null, call idmax retrieve with sessionId (edge mode)
          if (!localRequestId) {
            console.log('Exchange returned null, calling idmax retrieve with sessionId');
            const resultsAccessKey = urlParams.get('resultsAccessKey');
            const retrieveResponse = await fetch(getEdgeRetrieveUrl(), {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ sessionId: session_id, trinsic: true, code: resultsAccessKey }),
            });
            if (retrieveResponse.ok) {
              const retrieveResult = await retrieveResponse.json();
              console.log('IDMax retrieve response:', retrieveResult);
              // Let the window close itself after retrieve completes
              window.close();
              return;
            } else {
              console.error('IDMax retrieve failed:', retrieveResponse.status);
              setError('Could not complete verification');
              setIsLoading(false);
              return;
            }
          }
        } catch (err) {
          setError('Could not exchange session for token (Trinsic)');
          setIsLoading(false);
          return;
        }
      } else if (localRequestId?.endsWith('-edge')) {
        localRequestId = localRequestId.replace('-edge', '');
      }

      const maxRetries = useEdgeEndpoint ? 30 : 3; // Edge mode gets more retries
      const retryDelay = 2000; // 2 seconds between retries
      let retryCount = 0;
      let result = null;

      while (retryCount < maxRetries) {
        result = await fetchAndCheck(getPrimaryUrl, localRequestId, useEdgeEndpoint);

        // If we got data or already_done, break out
        if (result && !result.notFound) {
          break;
        }

        // If primary failed, try secondary
        if (!result || result.notFound) {
          console.warn(`Primary URL failed (attempt ${retryCount + 1}/${maxRetries}), trying secondary URL: ${getSecondaryUrl}`);
          result = await fetchAndCheck(getSecondaryUrl, localRequestId, useEdgeEndpoint);

          // If secondary also returned data, break out
          if (result && !result.notFound) {
            break;
          }
        }

        // If both returned notFound and we're in edge mode, retry after delay
        if (result?.notFound && useEdgeEndpoint) {
          retryCount++;
          if (retryCount < maxRetries) {
            await new Promise(resolve => setTimeout(resolve, retryDelay));
            continue;
          }
        }

        // Otherwise break out
        break;
      }

      if (!result || result.notFound) {
        console.error("Failed to fetch data from both primary and secondary URLs after retries. result:", result);
        setError("Failed to load session data");
        setIsLoading(false);
        return;
      }


      // Check if session is already done
      if (result.already_done === true) {
        setError("ALREADY_DONE");
        setIsLoading(false);
        return;
      }

      setSessionData(result);
      setIsLoading(false);
    };

    fetchData();
  }, [request_id]);

  // Send postMessage for ALREADY_DONE - must be before any conditional returns
  React.useEffect(() => {
    if (error === "ALREADY_DONE") {

      // Send payload version
      const payload = {
        messageType: "payload",
        value: {
          event: "ERROR",
          app: null,
          error: "ALREADY DONE"
        }
      };
      window.parent.postMessage(payload, "*");

      // Send simple string version
      window.parent.postMessage("ALREADY DONE", "*");
    }
  }, [error]);

  if (isLoading) {
    return (
      <Box sx={{ position: 'relative', height: '100vh' }}>
        <LoadingOverlay visible={true} overlayBlur={2} />
      </Box>
    );
  }

  if (error) {
    const errorTitle = error === "ALREADY_DONE" ? "Link Expired" : "Error";
    const errorMessage = error === "ALREADY_DONE"
      ? "This link is either invalid, expired, or has already been used"
      : error;

    return (
      <Center style={{ height: '100vh', flexDirection: 'column', padding: '20px' }}>
        <Text color="red" size="xl" mb={10}>❌</Text>
        <Text size="xl" weight={700} mb={10}>{errorTitle}</Text>
        <Text align="center">{errorMessage}</Text>
      </Center>
    );
  }

  // Determine if fingerprinting should be disabled based on URL param or settings
  // Default to true if is_fingerprint_active is not explicitly set to false
  const isFingerPrintActive = sessionData?.data?.settings?.is_fingerprint_active !== false;
  const fingerprintDisabled = shouldDisableFingerprint || !isFingerPrintActive;

  // Check for test mode from session data (edge mode)
  const isTestMode = sessionData?.test_mode === true;

  // Test mode banner component
  const TestModeBanner = () => (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        backgroundColor: '#ff9800',
        color: '#000',
        textAlign: 'center',
        padding: '2px 8px',
        fontSize: '10px',
        fontWeight: 'bold',
        zIndex: 99999,
      }}
    >
      Test Mode Active
    </div>
  );

  // Skip FpjsProvider when disabled via URL param or settings
  if (fingerprintDisabled) {
    return (
      <>
        {isTestMode && <TestModeBanner />}
        <DataProvider
          request_id={request_id}
          fingerprintDisabled={true}
          initialSessionData={sessionData}
        >
          <App routes={<SubRoutes />} />
        </DataProvider>
      </>
    );
  }

  // Regular flow with fingerprinting
  return (
    <>
      {isTestMode && <TestModeBanner />}
      <FpjsProvider
      loadOptions={{
        apiKey: "U1OoU5FUoWuUW1gJvyL3",
        endpoint: [
          "https://dcams.app/uTU6pIMQFwWuQOfC/AWn7OalBYI7aYzip",
          FingerprintJSPro.defaultEndpoint,
        ],
        scriptUrlPattern: [
          "https://dcams.app/uTU6pIMQFwWuQOfC/9N08WXSYlPMmL3GS?apiKey=<apiKey>&version=<version>&loaderVersion=<loaderVersion>",
          FingerprintJSPro.defaultScriptUrlPattern,
        ],
      }}
    >
      <DataProvider
        request_id={request_id}
        fingerprintDisabled={false}
        initialSessionData={sessionData}
      >
        <App routes={<SubRoutes />} />
      </DataProvider>
    </FpjsProvider>
    </>
  );
};

const RootComponent = () => {
  return (
    <Router>
      {/* Sentry error boundary is currently disabled
      <Sentry.ErrorBoundary fallback={<div>An error has occurred.</div>}>
      */}
        <Routes>
          <Route path="/:request_id/*" element={<AppWithData />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
      {/* </Sentry.ErrorBoundary> */}
    </Router>
  );
};

export default RootComponent;
